import { KuxFilteredCategoriesDTO, KuxMediaAssetType } from '@kaltura-ott/tvpil';
import { computed, observable } from 'mobx';
import { container } from 'tsyringe';

import { RatingInfo, ratingMetaKeys } from '../../utils/rate';
import type { AppStore } from '../app/app-store';
import type { DmsStore } from '../dms/dms-store';
import { EntityRating } from '../entities/entities.types';
import { LanguageKeyEnum } from '../localization/localization.store';
import { LocalizationStore } from '../localization/localization.store';

import { getParentalRatingString } from './parental.service';

const PROGRAM_META_MAX_COUNTRIES = 1;
const PROGRAM_META_MAX_GENRES = 3;
const PROGRAM_META_MAX_DIRECTORS = 3;
const PROGRAM_META_MAX_ACTORS = 4;
const PROGRAM_META_MAX_AUDIO_LANGUAGES = 4;
const PROGRAM_META_MAX_SUBTITLE_LANGUAGES = 4;

export interface ContentMarker {
  text: string;
  background?: string;
  color?: string;
}

export interface BingWatchingOffset {
  start: number;
  end: number;
}

export abstract class MediaAsset<T extends KuxMediaAssetType = KuxMediaAssetType> {
  @observable.ref data: T;

  countries?: string[] = undefined;
  countriesOfOrigin?: string[] = undefined;
  genres?: string[] = undefined;
  directors?: string[] = undefined;
  actors?: string[] = undefined;
  audioLanguage?: string[] = undefined;
  subtitleLanguage?: string[] = undefined;
  contentMarker?: ContentMarker = undefined;

  rootUiDesignElements?: KuxFilteredCategoriesDTO['uiDesignElements'];

  constructor(data: T) {
    const appStore = container.resolve<AppStore>('appStore');
    this.rootUiDesignElements = appStore.rootStore?.uiDesignElements;

    this.data = data;
    this.loadMeta(true);
  }

  abstract get isAdult(): boolean | undefined;

  @computed get isPurchased(): boolean | undefined {
    return undefined;
  }

  @computed get isPurchasable(): boolean | undefined {
    return undefined;
  }

  get id() {
    return this.data.id;
  }

  get allRatings(): RatingInfo[] {
    const ratings: RatingInfo[] = [];
    Object.entries(ratingMetaKeys).forEach(([ratingProvider, metaKey]) => {
      const rating = this.data.metas[metaKey];
      const stars = rating ? String(rating) : undefined;
      if (stars) {
        ratings.push({
          ratingProvider: ratingProvider,
          presentRatingSourceLogo: true,
          stars,
        });
      }
    });
    return ratings;
  }

  getCountries(limit = PROGRAM_META_MAX_COUNTRIES, force = false): string[] {
    if (this.countries && !force) return this.countries;
    this.countries = this.data?.tags?.Country?.slice(0, limit) || [];
    return this.countries;
  }

  getCountriesOfOrigin(limit = PROGRAM_META_MAX_COUNTRIES, force = false): string[] {
    if (this.countriesOfOrigin && !force) return this.countriesOfOrigin;
    this.countriesOfOrigin = this.data?.tags?.CountryOfOrigin?.slice(0, limit) || [];
    return this.countriesOfOrigin;
  }

  getGenres(limit = PROGRAM_META_MAX_GENRES, force = false): string[] {
    if (this.genres && !force) return this.genres;
    this.genres = this.data?.tags?.Genre?.slice(0, limit) || [];
    return this.genres;
  }

  getDirectors(limit = PROGRAM_META_MAX_DIRECTORS, force = false): string[] {
    if (this.directors && !force) return this.directors;
    this.directors = this.data?.tags?.Director?.slice(0, limit) || [];
    return this.directors;
  }

  getActors(limit = PROGRAM_META_MAX_ACTORS, force = false): string[] {
    if (this.actors && !force) return this.actors;
    this.actors = this.data?.tags?.Actors?.slice(0, limit) || [];
    return this.actors;
  }

  moveToTop(items: string[], search: string | undefined): string[] {
    if (search) {
      const index = items.findIndex((language) => language === search);
      if (index !== -1) {
        items.unshift(items.splice(index, 1)[0]);
      }
    }
    return items;
  }

  getAudioLanguages(limit = PROGRAM_META_MAX_AUDIO_LANGUAGES, force = false): string[] {
    if (!this.data) return [];
    if (this.audioLanguage && !force) return this.audioLanguage;
    const localizationStore = container.resolve<LocalizationStore>('localizationStore');

    const userLanguage = localizationStore?.activeLanguageByKey(LanguageKeyEnum.audio);
    this.audioLanguage = this.moveToTop(this.data?.tags?.AudioLanguage?.slice() || [], userLanguage?.name).slice(
      0,
      limit,
    );
    return this.audioLanguage;
  }

  getSubtitleLanguages(limit = PROGRAM_META_MAX_SUBTITLE_LANGUAGES, force = false): string[] {
    if (!this.data) return [];
    if (this.subtitleLanguage && !force) return this.subtitleLanguage;
    const localizationStore = container.resolve<LocalizationStore>('localizationStore');

    const userLanguage = localizationStore?.activeLanguageByKey(LanguageKeyEnum.subtitle);
    this.subtitleLanguage = this.moveToTop(this.data?.tags?.SubtitleLanguage?.slice() || [], userLanguage?.name).slice(
      0,
      limit,
    );
    return this.subtitleLanguage;
  }

  getContentMarker(): ContentMarker | undefined {
    if (!this.data) return undefined;
    if (!this.data.metas['ContentMarkerText']) return undefined;
    const rootBackground = this.rootUiDesignElements ? this.rootUiDesignElements['backgroundColor'] : undefined;
    const rootTextColor = this.rootUiDesignElements ? this.rootUiDesignElements['textColor'] : undefined;

    return {
      text: this.data.metas['ContentMarkerText'] as string,
      background: (this.data.metas['MarkerBackgroundColor'] as string) || rootBackground,
      color: (this.data.metas['MarkerTextColor'] as string) || rootTextColor,
    };
  }

  @computed public get parentalRatingString(): string | undefined {
    return getParentalRatingString(this.data);
  }

  loadMeta(force = false) {
    this.countries = this.getCountries(undefined, force);
    this.countriesOfOrigin = this.getCountriesOfOrigin(undefined, force);
    this.genres = this.getGenres(undefined, force);
    this.directors = this.getDirectors(undefined, force);
    this.actors = this.getActors(undefined, force);
    this.audioLanguage = this.getAudioLanguages(undefined, force);
    this.subtitleLanguage = this.getSubtitleLanguages(undefined, force);
    this.contentMarker = this.getContentMarker();
  }

  /* BINGE WATCHING */

  /**
   * Get creditStartOffset to identify when we should show NEXT EPISODE button (view)
   */
  @computed get creditStartOffset(): number | undefined {
    const creditOffset = Number(this.data.metas['CreditStartOffset']);

    if (isNaN(creditOffset)) {
      return undefined;
    } else {
      return creditOffset;
    }
  }

  /**
   * Get introOffset to identify when we should show SKIP INTRO button
   * @return { start: number, end: number } or undefined when hasn't defined
   */
  @computed get introOffset(): BingWatchingOffset | undefined {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    const { bingeWatching } = dmsStore.dms;
    if (!bingeWatching.enableBingeWatchingUi) {
      return undefined;
    }
    const introOffset: BingWatchingOffset = { start: 0, end: 0 };
    if (this.data?.metas['IntroStartOffset']) {
      introOffset.start = Number(this.data.metas.IntroStartOffset) * 1000;
    }
    if (this.data?.metas['IntroEndOffset']) {
      introOffset.end = Number(this.data.metas.IntroEndOffset) * 1000;
    }
    if (isNaN(introOffset.start) || isNaN(introOffset.end) || introOffset.start >= introOffset.end) {
      return undefined;
    }
    return introOffset;
  }

  /**
   * Get skipIntroMaxCountdownTime to set maximum countdown time to auto click SKIP INTRO button
   */
  @computed get skipIntroMaxCountdownTime(): number {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const { bingeWatching } = dmsStore.dms;
    if (!bingeWatching.enableBingeWatchingUi) {
      return -1;
    }
    return (Number(bingeWatching.skipIntroMaxCountdownTime) || 7) * 1000;
  }

  /**
   * Get recapOffset to identify when we should show SKIP RECAP button
   * @return { start: number, end: number } or undefined when hasn't defined
   */
  @computed get recapOffset(): BingWatchingOffset | undefined {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    const { bingeWatching } = dmsStore.dms;
    if (!bingeWatching.enableBingeWatchingUi) {
      return undefined;
    }
    const recapOffset: BingWatchingOffset = { start: 0, end: 0 };
    if (this.data?.metas['PreviousStartOffset']) {
      recapOffset.start = Number(this.data.metas.PreviousStartOffset) * 1000;
    }
    if (this.data?.metas['PreviousEndOffset']) {
      recapOffset.end = Number(this.data.metas.PreviousEndOffset) * 1000;
    }
    if (isNaN(recapOffset.start) || isNaN(recapOffset.end) || recapOffset.start >= recapOffset.end) {
      return undefined;
    }
    return recapOffset;
  }

  /**
   * Get skipRecapMaxCountdownTime to set maximum countdown time to auto click SKIP RECAP button
   */
  @computed get skipRecapMaxCountdownTime(): number {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    const { bingeWatching } = dmsStore.dms;
    if (!bingeWatching.enableBingeWatchingUi) {
      return -1;
    }
    return (Number(bingeWatching.skipRecapMaxCountdownTime) || 7) * 1000;
  }

  @computed public get parentalRating(): number {
    return this.data.parentalRating || 0;
  }

  @computed get rating(): EntityRating | undefined {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    const kuxRating = dmsStore.dms.ratingMap.find(
      ({ name }) => this.data.metas[name] && parseInt(this.data.metas[name] as unknown as string) > -1,
    );

    if (kuxRating) {
      return {
        ...kuxRating,
        value: this.data.metas[kuxRating.name] as unknown as string,
      };
    }
  }
}
